import { useMemo, useState } from 'react';
import { SOURCE_LANGUAGES, DIASPORA_CODES, COUNTRY_LABEL } from './course-countries';

// Full course-selection cascade for the Audio Course page:
//   Source language → Target language → Target country → Course → Level → Submit
//
// A CatalogEntry is one real, buildable course. `code` is the backend course-id (drives
// /scripts, /outputs, /build). `target` groups locale variants under one language in the UI
// (e.g. SPA_ES/SPA_MX/SPA_US all show under "Spanish"); `country` is the market that course
// serves (ISO-ish code matching course-countries.ts). `course` is the edition label.
//
// The country dropdown = the countries that actually have a course for the chosen target
// (from the catalog) UNIONED with the universal jurisdiction/diaspora guides from
// course-countries.ts, so the operator can also pick a market to COMMISSION a new course.
export type CatalogEntry = { code: string; target: string; country: string; course: string };

const fieldCls =
  'mt-1 rounded bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm text-gray-100 focus:border-accent-teal focus:outline-none disabled:opacity-50';

export default function CourseSettings({
  catalog, levels, current, onSubmit, submitLabel = 'Submit', busy = false,
}: {
  catalog: CatalogEntry[];
  levels: string[];
  current?: { code: string; level: string };
  onSubmit: (code: string, level: string, meta: { source: string; target: string; country: string; course: string; exists: boolean }) => void;
  submitLabel?: string;
  busy?: boolean;
}) {
  const entryOf = (code: string) => catalog.find((c) => c.code === code);
  const seed = current?.code ? entryOf(current.code) : undefined;

  const [source, setSource] = useState(SOURCE_LANGUAGES[0]?.code || 'EN');
  const [target, setTarget] = useState(seed?.target || '');
  const [country, setCountry] = useState(seed?.country || '');
  const [course, setCourse] = useState(seed?.course || '');
  const [level, setLevel] = useState(current?.level || levels[0] || 'A1');

  const targets = useMemo(
    () => [...new Set(catalog.map((c) => c.target))].sort((a, b) => a.localeCompare(b)),
    [catalog]);

  // Countries = the target language's OWN native country/countries (from the catalog) + the
  // universal {US, UK, Australia} diaspora, de-duped. Nothing else. `hasCourse` marks the ones
  // with a built course (the rest are commissionable markets).
  const countries = useMemo<{ code: string; label: string; hasCourse: boolean }[]>(() => {
    if (!target) return [];
    const native = [...new Set(catalog.filter((c) => c.target === target).map((c) => c.country))];
    const codes = [...new Set([...native, ...DIASPORA_CODES])];
    return codes.map((code) => ({ code, label: COUNTRY_LABEL[code] || code, hasCourse: native.includes(code) }));
  }, [catalog, target]);

  const coursesHere = useMemo(
    () => catalog.filter((c) => c.target === target && (!country || c.country === country)),
    [catalog, target, country]);

  const pickTarget = (t: string) => {
    setTarget(t); setCountry(''); setCourse('');
    const cs = catalog.filter((c) => c.target === t);
    const uniqCountries = [...new Set(cs.map((c) => c.country))];
    if (uniqCountries.length === 1) setCountry(uniqCountries[0]!);
  };
  const pickCountry = (c: string) => {
    setCountry(c);
    const cs = catalog.filter((e) => e.target === target && e.country === c);
    setCourse(cs.length === 1 ? cs[0]!.course : '');
  };

  const resolved = catalog.find((c) => c.target === target && c.country === country && c.course === course);
  const canSubmit = !!resolved && !busy;

  return (
    <section className="flex flex-wrap items-end gap-3">
      <label className="block">
        <span className="text-xs text-gray-400">Source language</span>
        <select value={source} onChange={(e) => setSource(e.target.value)} className={`${fieldCls} w-full sm:w-40 block`}>
          {SOURCE_LANGUAGES.map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
        </select>
      </label>
      <label className="block">
        <span className="text-xs text-gray-400">Target language</span>
        <select value={target} onChange={(e) => pickTarget(e.target.value)} className={`${fieldCls} w-full sm:w-48 block`}>
          <option value="">— choose —</option>
          {targets.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </label>
      <label className="block">
        <span className="text-xs text-gray-400">Target country</span>
        <select value={country} onChange={(e) => pickCountry(e.target.value)} disabled={!target} className={`${fieldCls} w-full sm:w-48 block`}>
          <option value="">{target ? '— choose —' : '— pick a language —'}</option>
          {countries.map((c) => (
            <option key={c.code} value={c.code}>{c.label}{c.hasCourse ? '' : ' (no course yet)'}</option>
          ))}
        </select>
      </label>
      <label className="block">
        <span className="text-xs text-gray-400">Course</span>
        <select value={course} onChange={(e) => setCourse(e.target.value)} disabled={!country} className={`${fieldCls} w-full sm:w-56 block`}>
          <option value="">{country ? (coursesHere.length ? '— choose —' : '— none built —') : '— pick a country —'}</option>
          {coursesHere.map((c) => <option key={c.code} value={c.course}>{c.course}</option>)}
        </select>
      </label>
      <label className="block">
        <span className="text-xs text-gray-400">Level</span>
        <select value={level} onChange={(e) => setLevel(e.target.value)} className={`${fieldCls} w-full sm:w-24 block`}>
          {levels.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
      </label>
      <button
        onClick={() => resolved && onSubmit(resolved.code, level, { source, target, country, course, exists: true })}
        disabled={!canSubmit}
        className="rounded bg-accent-teal/20 border border-accent-teal/40 text-accent-teal px-4 py-2 text-sm font-medium hover:bg-accent-teal/30 disabled:opacity-50">
        {busy ? 'Loading…' : submitLabel}
      </button>
      {current?.code && (
        <span className="text-xs text-gray-500 pb-2">
          Loaded: <b className="text-gray-300">{entryOf(current.code)?.course || current.code}</b> · {current.level}
        </span>
      )}
    </section>
  );
}
